/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useContext, useEffect } from 'react';
import { merge } from 'lodash';
import { BrowserRouter as Router } from 'react-router-dom';
import {
  contexts,
  services,
  unstable,
  utils
} from '@broadleaf/admin-components';
import baseMessages from '@broadleaf/admin-components/messages';
import customMessages from '../messages/index.json';
import { AuroraAdminProvider } from 'app/providers/AuroraAdminProvider';

// purgecss: tw-popover-transparent tw-popover-black tw-popover-white tw-popover-gray-100 tw-popover-gray-200 tw-popover-gray-300 tw-popover-gray-400 tw-popover-gray-500 tw-popover-gray-600 tw-popover-gray-700 tw-popover-gray-800 tw-popover-gray-900 tw-popover-red-100 tw-popover-red-200 tw-popover-red-300 tw-popover-red-400 tw-popover-red-500 tw-popover-red-600 tw-popover-red-700 tw-popover-red-800 tw-popover-red-900 tw-popover-orange-100 tw-popover-orange-200 tw-popover-orange-300 tw-popover-orange-400 tw-popover-orange-500 tw-popover-orange-600 tw-popover-orange-700 tw-popover-orange-800 tw-popover-orange-900 tw-popover-yellow-100 tw-popover-yellow-200 tw-popover-yellow-300 tw-popover-yellow-400 tw-popover-yellow-500 tw-popover-yellow-600 tw-popover-yellow-700 tw-popover-yellow-800 tw-popover-yellow-900 tw-popover-green-100 tw-popover-green-200 tw-popover-green-300 tw-popover-green-400 tw-popover-green-500 tw-popover-green-600 tw-popover-green-500 tw-popover-green-600 tw-popover-green-900 tw-popover-teal-100 tw-popover-teal-200 tw-popover-teal-300 tw-popover-teal-400 tw-popover-teal-500 tw-popover-teal-600 tw-popover-teal-700 tw-popover-teal-800 tw-popover-teal-900 tw-popover-blue-100 tw-popover-blue-200 tw-popover-blue-300 tw-popover-blue-400 tw-popover-blue-500 tw-popover-blue-600 tw-popover-blue-700 tw-popover-blue-800 tw-popover-blue-900 tw-popover-indigo-100 tw-popover-indigo-200 tw-popover-indigo-300 tw-popover-indigo-400 tw-popover-indigo-500 tw-popover-indigo-600 tw-popover-indigo-700 tw-popover-indigo-800 tw-popover-indigo-900 tw-popover-purple-100 tw-popover-purple-200 tw-popover-purple-300 tw-popover-purple-400 tw-popover-purple-500 tw-popover-purple-600 tw-popover-purple-700 tw-popover-purple-800 tw-popover-purple-900 tw-popover-pink-100 tw-popover-pink-200 tw-popover-pink-300 tw-popover-pink-400 tw-popover-pink-500 tw-popover-pink-600 tw-popover-pink-700 tw-popover-pink-800 tw-popover-pink-900 tw-popover-primary-100 tw-popover-primary-200 tw-popover-primary-300 tw-popover-primary-400 tw-popover-primary-500 tw-popover-primary-500 tw-popover-primary-500 tw-popover-primary-600 tw-popover-primary-900 tw-popover-secondary-100 tw-popover-secondary-200 tw-popover-secondary-300 tw-popover-secondary-400 tw-popover-secondary-500 tw-popover-secondary-600 tw-popover-secondary-700 tw-popover-secondary-800 tw-popover-secondary-900 tw-popover-link-100 tw-popover-link-200 tw-popover-link-300 tw-popover-link-400 tw-popover-link-500 tw-popover-link-600 tw-popover-link-700 tw-popover-link-800 tw-popover-link-900
// purgecss: tw-popover-top tw-popover-top-left tw-popover-top-right tw-popover-right tw-popover-right-top tw-popover-right-bottom tw-popover-bottom tw-popover-bottom-left tw-popover-bottom-right tw-popover-left tw-popover-left-top tw-popover-left-bottom
// purgecss: tw-bg-red-200 tw-text-red-500 tw-text-red-600 tw-bg-blue-200 tw-text-blue-500 tw-text-blue-600 tw-bg-green-200 tw-text-green-500 tw-text-green-600 tw-bg-orange-200 tw-text-orange-500 tw-text-orange-600
// purgecss: tw-bg-red-200 tw-text-red-500 tw-text-red-600 tw-bg-blue-200 tw-text-blue-500 tw-text-blue-600 tw-bg-green-200 tw-text-green-500 tw-text-green-600 tw-bg-orange-200 tw-text-orange-500 tw-text-orange-600
// purgecss: tw-bg-danger-600 hover:tw-bg-danger-700 tw-bg-primary-600 hover:tw-bg-primary-700

const { TenantContext } = contexts;
const { AuthService } = services;
const { Environment } = utils;
const { AdminApp } = unstable;
const messages = merge({}, baseMessages, customMessages);

const browserSupportsHistory = 'pushState' in window.history;
const App = () => {
  return (
    <Router
      basename={Environment.get('public.url')}
      forceRefresh={!browserSupportsHistory}
    >
      <AuroraAdminProvider messages={messages}>
        <ClearApplicationIdIfFleetAdmin>
          <AdminApp />
        </ClearApplicationIdIfFleetAdmin>
      </AuroraAdminProvider>
    </Router>
  );
};

const ClearApplicationIdIfFleetAdmin = ({ children }) => {
  const [state, dispatch] = useContext(TenantContext);
  useEffect(() => {
    const { user } = AuthService.getState();
    if (!!state.applicationId && isFleetAdmin(user)) {
      dispatch({
        type: 'SET_APPLICATION_ID',
        payload: {
          applicationId: null
        }
      });
    }
  }, [dispatch, state.applicationId]);
  return children;
};

function isFleetAdmin(user) {
  const roles = user.roles || [];
  for (const rolesKey in roles) {
    if (roles[rolesKey].name === 'ROLE_FLEET_ADMIN') {
      return true;
    }
  }
  return false;
}
export default App;
